/**
 * JarvisFAB — always-on Iron Man-style AI operating partner.
 *
 * Emerald pulsing FAB in the bottom-right corner of every V3 page.
 * Click → slide-out chat panel. Type → POST /api/jarvis/chat.
 *
 * Design intent:
 *   - Feels sentient, not spammy. Idle pulse is subtle.
 *   - Panel width is comfortable for reading paragraphs (380px on desktop).
 *   - Dark chat header (Jarvis blue-emerald gradient) grounds the persona.
 *   - Message bubbles: user right (slate), Jarvis left (emerald tint).
 *   - Mobile: panel takes full width with rounded top corners.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, X, Send, Loader2, Zap, Volume2, VolumeX, Mic, Square, Sunrise, Moon,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActiveProperty } from "@/context/ActivePropertyContext";

function MessageBubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`jarvis-msg-${role}`}
    >
      <div
        className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-slate-900 text-white rounded-tr-sm"
            : "bg-emerald-50 text-slate-900 border border-emerald-100 rounded-tl-sm"
        }`}
      >
        {text}
      </div>
    </div>
  );
}

export default function JarvisFAB() {
  const { user } = useAuth();
  const { propertyId: activePropertyId, selected: activeProperty } = useActiveProperty();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem("jarvis-voice") !== "off"; }
    catch { return true; }
  });
  const [pttOn, setPttOn] = useState(() => {
    try { return localStorage.getItem("jarvis-ptt") !== "off"; }
    catch { return true; }
  });
  const [emailBriefingOn, setEmailBriefingOn] = useState(false);
  const [emailWrapOn, setEmailWrapOn] = useState(false);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);

  // Load current 7am / 7pm email opt-in state (once, when user opens panel)
  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/jarvis/briefing/settings");
        if (!cancelled) {
          setEmailBriefingOn(Boolean(data?.daily_briefing_email));
          setEmailWrapOn(Boolean(data?.evening_wrap_email));
        }
      } catch { /* silent — not critical */ }
    })();
    return () => { cancelled = true; };
  }, [open, user]);

  const toggleEmailBriefing = useCallback(async () => {
    const next = !emailBriefingOn;
    setEmailBriefingOn(next);  // optimistic
    try {
      await api.post("/jarvis/briefing/settings", { daily_briefing_email: next });
      toast.success(next ? "You'll get a Jarvis briefing every morning at 7am." : "7am email briefing off.");
    } catch {
      setEmailBriefingOn(!next);  // revert
      toast.error("Couldn't update your preference. Try again in a moment.");
    }
  }, [emailBriefingOn]);

  const toggleEmailWrap = useCallback(async () => {
    const next = !emailWrapOn;
    setEmailWrapOn(next);  // optimistic
    try {
      await api.post("/jarvis/briefing/settings", { evening_wrap_email: next });
      toast.success(next ? "You'll get a Jarvis wrap every evening at 7pm." : "7pm evening wrap off.");
    } catch {
      setEmailWrapOn(!next);  // revert
      toast.error("Couldn't update your preference. Try again in a moment.");
    }
  }, [emailWrapOn]);

  const stopSpeech = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = "";
      } catch { /* noop */ }
      audioRef.current = null;
    }
    // Also cancel any legacy browser TTS still queued
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  }, []);

  const speak = useCallback(async (text) => {
    if (!voiceOn || !text) return;
    stopSpeech();
    try {
      // Fetch ElevenLabs MP3 (Jarvis British voice) via authenticated endpoint
      const resp = await api.post("/jarvis/voice", { text }, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); };
      audio.onerror = () => { URL.revokeObjectURL(url); };
      audioRef.current = audio;
      await audio.play();
    } catch {
      // ElevenLabs unavailable — fall back to browser TTS so Jarvis still speaks.
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        const utter = new SpeechSynthesisUtterance(text);
        utter.rate = 1.05;
        utter.pitch = 0.95;
        window.speechSynthesis.speak(utter);
      }
    }
  }, [voiceOn, stopSpeech]);

  const toggleVoice = useCallback(() => {
    setVoiceOn(v => {
      const next = !v;
      try { localStorage.setItem("jarvis-voice", next ? "on" : "off"); } catch { /* noop */ }
      if (!next) stopSpeech();
      return next;
    });
  }, [stopSpeech]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/jarvis/history?limit=30");
      const flat = [];
      for (const t of data.turns || []) {
        if (t.user_message) flat.push({ role: "user", text: t.user_message });
        if (t.assistant_message) flat.push({ role: "assistant", text: t.assistant_message });
      }
      setMessages(flat);
    } catch {
      /* no history yet — fine */
    }
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) loadHistory();
  }, [open, messages.length, loadHistory]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = useCallback(async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;
    if (!overrideText) setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);

    // Route "brief me" phrases to morning briefing, "wrap me" phrases to evening wrap
    const morningTrigger = /^(give me (my )?daily briefing|brief me|daily briefing|morning briefing|what'?s on today)\.?$/i;
    const eveningTrigger = /^(give me (my )?evening wrap|wrap me up|wrap it up|evening wrap|how'?d today go|end of day)\.?$/i;
    let briefingMode = null;
    if (morningTrigger.test(text)) briefingMode = "morning";
    else if (eveningTrigger.test(text)) briefingMode = "evening";

    try {
      let data;
      if (briefingMode) {
        ({ data } = await api.post(`/jarvis/briefing?mode=${briefingMode}`));
      } else {
        ({ data } = await api.post("/jarvis/chat", {
          message: text,
          // ReSimpli unification: always send the top-bar Active Property
          // so Jarvis knows which deal "this" refers to.
          property_id: activePropertyId || undefined,
        }));
      }
      const reply = data.reply || "…";
      setMessages((m) => [...m, { role: "assistant", text: reply }]);
      speak(reply);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something crossed the wires. Try again in a moment.";
      setMessages((m) => [...m, { role: "assistant", text: `Apologies, ${typeof detail === "string" ? detail : detail.message || "unavailable"}` }]);
      toast.error("Jarvis is momentarily off-line.");
    } finally {
      setSending(false);
    }
  }, [input, sending, speak]);

  // One-click Daily Briefing (morning) + Evening Wrap
  const requestBriefing = useCallback(() => {
    send("Give me my daily briefing.");
  }, [send]);
  const requestWrap = useCallback(() => {
    send("Give me my evening wrap.");
  }, [send]);

  // ── Voice INPUT (mic → Whisper → chat) ─────────────────────────────
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recStreamRef = useRef(null);
  const recTimerRef = useRef(null);

  const stopRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === "inactive") return;
    try { rec.stop(); } catch { /* noop */ }
    if (recTimerRef.current) { clearTimeout(recTimerRef.current); recTimerRef.current = null; }
  }, []);

  const startRecording = useCallback(async () => {
    if (recording || transcribing || sending) return;
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Your browser doesn't support voice input. Try Chrome, Safari, or Edge.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recStreamRef.current = stream;
      // Pick the most compatible container/codec available
      let mimeType = "";
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
      for (const m of candidates) {
        if (window.MediaRecorder.isTypeSupported?.(m)) { mimeType = m; break; }
      }
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      audioChunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        // Cleanup mic stream
        try { recStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
        recStreamRef.current = null;
        setRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: mimeType || "audio/webm" });
        audioChunksRef.current = [];
        if (blob.size < 800) { toast.error("Didn't catch that — hold to speak."); return; }
        // Upload to Whisper
        setTranscribing(true);
        try {
          const fd = new FormData();
          const ext = (mimeType.includes("mp4") ? "mp4" : mimeType.includes("mpeg") ? "mp3" : "webm");
          fd.append("audio", blob, `jarvis-${Date.now()}.${ext}`);
          const { data } = await api.post("/jarvis/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (data?.text || "").trim();
          if (!text) { toast.error("No speech detected."); return; }
          // Auto-send transcribed text
          setInput("");
          await send(text);
        } catch (err) {
          const detail = err?.response?.data?.detail || "Transcription failed.";
          toast.error(typeof detail === "string" ? detail : "Transcription failed.");
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      setRecording(true);
      // Safety auto-stop after 60s
      recTimerRef.current = setTimeout(() => { stopRecording(); }, 60_000);
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        toast.error("Microphone access blocked. Enable it in browser settings and retry.");
      } else {
        toast.error("Couldn't start recording. Check your microphone.");
      }
    }
  }, [recording, transcribing, sending, send, stopRecording]);

  // Cleanup on unmount
  useEffect(() => () => {
    stopRecording();
    try { recStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
  }, [stopRecording]);

  // ── Push-to-Talk: hold spacebar anywhere in the app to talk to Jarvis
  const pttHoldingRef = useRef(false);
  useEffect(() => {
    if (!pttOn || !user) return undefined;

    const isTypingContext = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (el.isContentEditable) return true;
      // Any ancestor contenteditable
      return !!el.closest?.('[contenteditable="true"]');
    };

    const onDown = (e) => {
      if (e.code !== "Space" || e.repeat) return;
      if (isTypingContext(e.target)) return;
      if (pttHoldingRef.current) return;
      if (recording || transcribing || sending) return;
      e.preventDefault();  // block page scroll
      pttHoldingRef.current = true;
      setOpen(true);       // slide the panel in so user sees the flow
      startRecording();
    };
    const onUp = (e) => {
      if (e.code !== "Space") return;
      if (!pttHoldingRef.current) return;
      pttHoldingRef.current = false;
      e.preventDefault();
      stopRecording();     // triggers upload → transcribe → auto-send
    };
    window.addEventListener("keydown", onDown, { capture: true });
    window.addEventListener("keyup", onUp, { capture: true });
    return () => {
      window.removeEventListener("keydown", onDown, { capture: true });
      window.removeEventListener("keyup", onUp, { capture: true });
    };
  }, [pttOn, user, recording, transcribing, sending, startRecording, stopRecording]);

  const togglePtt = useCallback(() => {
    setPttOn((prev) => {
      const next = !prev;
      try { localStorage.setItem("jarvis-ptt", next ? "on" : "off"); } catch { /* noop */ }
      toast.success(next ? "Push-to-talk on — hold Space to speak" : "Push-to-talk off");
      return next;
    });
  }, []);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Hidden until user is authenticated — public pages don't need Jarvis
  if (!user) return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-[9998] group flex items-center gap-2.5 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white pl-2.5 pr-4 py-2.5 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/60 hover:scale-[1.04] active:scale-[0.98] transition-all"
          data-testid="jarvis-fab"
          aria-label="Open Jarvis"
        >
          {/* Arc-reactor orb */}
          <span className="jarvis-orb relative flex items-center justify-center w-9 h-9 rounded-full">
            <span className="jarvis-orb-ring" />
            <span className="jarvis-orb-ring-2" />
            <span className="jarvis-orb-core">
              <Sparkles size={14} className="jarvis-orb-icon" />
            </span>
          </span>
          <span className="text-sm font-bold tracking-tight hidden sm:inline">Jarvis</span>
        </button>
      )}

      {/* Slide-out chat panel */}
      {open && (
        <div
          className="fixed z-[9999] bottom-0 right-0 md:bottom-6 md:right-6 md:rounded-2xl w-full md:w-[380px] h-[70vh] md:h-[540px] flex flex-col bg-white shadow-2xl border border-slate-200 md:border md:rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200"
          data-testid="jarvis-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-900 to-emerald-900 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className={`jarvis-orb relative flex items-center justify-center w-9 h-9 rounded-full ${sending ? "jarvis-orb-thinking" : ""}`}>
                <span className="jarvis-orb-ring" />
                <span className="jarvis-orb-ring-2" />
                <span className="jarvis-orb-core">
                  <Sparkles size={14} className="jarvis-orb-icon" />
                </span>
              </span>
              <div className="leading-tight">
                <div className="font-bold text-sm flex items-center gap-1.5">
                  Jarvis <Zap size={11} className="text-emerald-300" />
                </div>
                <div className="text-[10px] text-emerald-200/80 uppercase tracking-widest">
                  {sending ? "Thinking…" : "AI Operating Partner"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={togglePtt}
                className={`text-white/70 hover:text-white p-1.5 rounded-md hover:bg-white/10 transition-colors relative ${pttOn ? "text-emerald-300" : ""}`}
                data-testid="jarvis-ptt-toggle"
                aria-label={pttOn ? "Disable push-to-talk" : "Enable push-to-talk"}
                title={pttOn ? "Push-to-talk ON — hold Space to speak" : "Push-to-talk OFF"}
              >
                <span className="text-[10px] font-bold tracking-wider">PTT</span>
                {pttOn && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-300" />}
              </button>
              <button
                onClick={toggleVoice}
                className="text-white/70 hover:text-white p-1.5 rounded-md hover:bg-white/10 transition-colors"
                data-testid="jarvis-voice-toggle"
                aria-label={voiceOn ? "Mute Jarvis voice" : "Un-mute Jarvis voice"}
                title={voiceOn ? "Voice on — click to mute" : "Voice off — click to enable"}
              >
                {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
              <button
                onClick={() => {
                  stopSpeech();
                  setOpen(false);
                }}
                className="text-white/70 hover:text-white p-1"
                data-testid="jarvis-close"
                aria-label="Close Jarvis"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Active property scope pill — tells the operator that Jarvis
              already knows which deal they're working on. */}
          {activeProperty && (
            <div
              data-testid="jarvis-active-property-scope"
              className="px-4 py-2 border-b border-white/5 bg-slate-900/60 text-[11px] text-slate-300 flex items-center gap-1.5"
            >
              <span className="uppercase tracking-widest font-black text-amber-400 text-[9px]">Scoped to</span>
              <span className="truncate font-semibold text-white">
                {activeProperty.address?.street || "Untitled"}
                {activeProperty.address?.state ? ` · ${activeProperty.address.state}` : ""}
              </span>
            </div>
          )}

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/40"
            data-testid="jarvis-messages"
          >
            {messages.length === 0 && !sending && (
              <div className="text-center py-8">
                <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                  <Sparkles size={20} className="text-emerald-600" />
                </div>
                <div className="text-sm font-bold text-slate-900">At your service, Tracy.</div>
                <div className="text-xs text-slate-500 mt-1 max-w-[240px] mx-auto leading-relaxed">
                  Ask about your properties, run quick math, or have me draft a message.
                </div>
                <div className="mt-4 space-y-1.5 text-[11px] text-slate-500">
                  <div className="italic">&ldquo;What&apos;s my highest-scored property?&rdquo;</div>
                  <div className="italic">&ldquo;MAO if ARV is $220k and repairs are $28k?&rdquo;</div>
                  <div className="italic">&ldquo;Draft an SMS for a cash buyer follow-up.&rdquo;</div>
                </div>
                <div className="mt-5 flex flex-col items-center gap-2">
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                      onClick={requestBriefing}
                      disabled={sending}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 text-white text-xs font-bold shadow-md hover:shadow-lg hover:scale-[1.03] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                      data-testid="jarvis-daily-briefing"
                    >
                      <Sunrise size={14} />
                      Daily Briefing
                    </button>
                    <button
                      onClick={requestWrap}
                      disabled={sending}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-indigo-600 via-purple-700 to-slate-900 text-white text-xs font-bold shadow-md hover:shadow-lg hover:scale-[1.03] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                      data-testid="jarvis-evening-wrap"
                    >
                      <Moon size={14} />
                      Evening Wrap
                    </button>
                  </div>
                  <div className="flex flex-col items-center gap-1 mt-1">
                    <button
                      onClick={toggleEmailBriefing}
                      className={`text-[10px] font-semibold uppercase tracking-wider inline-flex items-center gap-1.5 hover:underline transition-colors ${
                        emailBriefingOn ? "text-amber-700" : "text-slate-500"
                      }`}
                      data-testid="jarvis-briefing-email-toggle"
                      aria-pressed={emailBriefingOn}
                      title={emailBriefingOn ? "Turn off 7am email" : "Get this in my inbox at 7am"}
                    >
                      <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                        emailBriefingOn ? "bg-amber-500 border-amber-500 text-white" : "border-slate-300 bg-white"
                      }`}>
                        {emailBriefingOn ? "✓" : ""}
                      </span>
                      Email me the briefing at 7am
                    </button>
                    <button
                      onClick={toggleEmailWrap}
                      className={`text-[10px] font-semibold uppercase tracking-wider inline-flex items-center gap-1.5 hover:underline transition-colors ${
                        emailWrapOn ? "text-indigo-700" : "text-slate-500"
                      }`}
                      data-testid="jarvis-wrap-email-toggle"
                      aria-pressed={emailWrapOn}
                      title={emailWrapOn ? "Turn off 7pm email" : "Get this in my inbox at 7pm"}
                    >
                      <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                        emailWrapOn ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300 bg-white"
                      }`}>
                        {emailWrapOn ? "✓" : ""}
                      </span>
                      Email me the wrap at 7pm
                    </button>
                  </div>
                </div>
                {pttOn && (
                  <div className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                    <Mic size={10} /> Hold <kbd className="px-1 py-0.5 rounded bg-white border border-emerald-300 text-emerald-800 font-mono">Space</kbd> to talk
                  </div>
                )}
              </div>
            )}
            {messages.map((m, i) => (
              <MessageBubble key={i} role={m.role} text={m.text} />
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="jarvis-typing">
                <div className="px-3.5 py-2 rounded-2xl rounded-tl-sm bg-emerald-50 border border-emerald-100">
                  <Loader2 size={14} className="animate-spin text-emerald-600" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-100 bg-white px-3 py-3 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={recording ? "Listening…" : transcribing ? "Transcribing…" : "Ask Jarvis anything…"}
              rows={1}
              className="flex-1 resize-none rounded-lg border border-slate-200 focus:border-slate-900 outline-none px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 max-h-24 transition-colors"
              data-testid="jarvis-input"
              disabled={sending || recording || transcribing}
            />
            {/* Mic (voice input) */}
            <button
              onClick={recording ? stopRecording : startRecording}
              disabled={sending || transcribing}
              className={`rounded-lg px-3 py-2 shrink-0 transition-colors border ${
                recording
                  ? "bg-rose-600 hover:bg-rose-700 text-white border-rose-600 animate-pulse"
                  : transcribing
                  ? "bg-slate-100 text-slate-400 border-slate-200"
                  : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
              }`}
              data-testid={recording ? "jarvis-mic-stop" : "jarvis-mic-start"}
              aria-label={recording ? "Stop recording" : "Start voice input"}
              title={recording ? "Tap to stop" : "Voice input"}
            >
              {transcribing ? <Loader2 size={16} className="animate-spin" />
                : recording ? <Square size={16} fill="currentColor" />
                : <Mic size={16} />}
            </button>
            <button
              onClick={() => send()}
              disabled={!input.trim() || sending || recording || transcribing}
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 disabled:opacity-40 transition-colors shrink-0"
              data-testid="jarvis-send"
              aria-label="Send"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
